import { useEffect, useState } from "react";
import { featuredStories, type FeatureStory } from "@/mocks/suye";
import SectionHeading from "./SectionHeading";

const storyBody: Record<string, string[]> = {
  s1: [
    "开票当天，十秒售罄的截图在社交平台刷屏。紧随其后的，是二手平台上一路飙升的转卖价——不少粉丝一边抢票一边忍不住点开转卖链接，看同一个座位被标出了几倍的价格。",
    "顺着这条链路复盘，问题往往不出在“票少”，而出在放票节奏与转卖核验之间。主办方放出的批次、退票回流的时间点、平台对异常账号的识别，每一个环节都可能成为黄牛的窗口。",
    "当实名制与转卖平台“可代拍”功能并存时，约束效果难免打折扣。真正要堵住的，是同一批货在不同池子之间反复流通造成的溢价空间。",
    "至于这场风波会不会催生更严格的票务规则，还取决于观众用脚投票的力度。至少在眼下，理性转手、拒绝高价接盘，是普通人能做的第一道防线。",
  ],
  s2: [
    "过去一年，越来越多国产团队把目光投向海外：从美术外包、独立发行到自研买断，出海方式早已不是单一模板。真正跑出来的，往往是懂玩法也懂本地化的人。",
    "复盘几款有代表性的作品可以发现，它们在海外爆发的共同点，不是靠砸买量，而是把“文化细节”做进了可玩的系统里——海外的玩家先被玩法吸引，再顺藤摸瓜去了解故事背后的文化。",
    "当然，出海也不是一路坦途。语言本地化、合规、售后与社区运营，都是很多团队第一次踩坑的地方。谁能把服务的最后一公里补齐，谁就更能留住第一批海外玩家。",
    "当中国游戏逐渐从“代工”走向“原创品牌”，出海就不再只是寻找增量，而是一场关于长期信任的积累。",
  ],
  s3: [
    "城市漫步的热，是从一张张“无目的散步”的打卡照开始的。人们不再把周末塞满景点，而是愿意花一下午，在一座街区里漫无目的地走。",
    "沿着热搜往外看，真正受益的是那些藏在街巷里的小生意：二手书店延长了营业时间，社区咖啡馆把临街的窗户换成折叠门，杂货铺老板学会在门口支起几把椅子。",
    "小店店主坦言客流确实翻了倍，但也担心来得快去得也快——当“打卡”变成新的 KPI，松弛感本身就容易被消费掉。",
    "一种更健康的街区经济，应当是“流量把人带来，内容把人留住”。当人们愿意为一份家常咖啡和一本旧书二刷三刷时，这条街才算真正活了过来。",
  ],
  s4: [
    "从早年的“吃瓜”到如今的“已读乱回”，网络流行语的更替速度肉眼可见地变快了。每一个新梗诞生，几乎都在几天内走完“出现—刷屏—被玩烂—被考古”的全周期。",
    "把时间线拉长看，这些梗共享同一套语言本能：用极短的词，表达复杂的社会情绪。它既是年轻人交流的暗号，也是观察当下心态的一扇窗。",
    "语言学家提醒，梗文化的快，不代表表达的浅。真正能留下来的梗，往往替一代人说出了没说出口的感受——这也是为什么有些梗过了几年还会被重新考古。",
    "下次再听到新梗时不妨多问一句：它到底在替大家表达什么？答案，往往比段子本身更有意思。",
  ],
};

export default function FeaturedStories() {
  const [openId, setOpenId] = useState<string | null>(null);
  const current = featuredStories.find((s) => s.id === openId) || null;
  const [lead, ...rest] = featuredStories;

  useEffect(() => {
    if (!openId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenId(null);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openId]);

  return (
    <section id="feature" className="border-b border-background-200 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <SectionHeading
          kicker="热门专题"
          title={
            <>
              热点背后的<span className="text-primary-600">完整故事</span>
            </>
          }
          desc="不只看热闹，也理得清门道。点击卡片即可展开深度全文。"
          action={
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-800">
              <i className="ri-book-open-line" />
              深度阅读专区
            </span>
          }
        />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <button
            type="button"
            onClick={() => setOpenId(lead.id)}
            className="group col-span-1 flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-foreground-950 text-left lg:col-span-2 lg:flex-row"
          >
            <div className="relative min-h-[240px] w-full overflow-hidden lg:w-1/2 lg:min-h-[340px]">
              <img
                src={lead.image}
                alt={lead.title}
                className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-1 flex-col justify-center p-6 md:p-8">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-500 px-2.5 py-0.5 text-[11px] font-bold text-background-50">
                  专题精选
                </span>
                <span className="rounded-full bg-background-50/10 px-2.5 py-0.5 text-[11px] font-medium text-background-200">
                  {lead.category}
                </span>
                <span className="text-xs text-background-300">
                  {lead.date} · {lead.readMin} 读完
                </span>
              </div>
              <h3 className="mt-4 font-heading text-xl font-black leading-snug text-background-50 md:text-2xl">
                {lead.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-background-200/90">{lead.excerpt}</p>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                {lead.tags.map((tg) => (
                  <span key={tg} className="rounded-md bg-background-50/10 px-2 py-0.5 text-[11px] text-background-200">
                    #{tg}
                  </span>
                ))}
                <span className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-accent-400 transition-colors group-hover:text-accent-300">
                  阅读全文 <i className="ri-arrow-right-line" />
                </span>
              </div>
            </div>
          </button>

          {rest.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setOpenId(s.id)}
              className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 text-left transition-colors hover:border-primary-200"
            >
              <div className="relative aspect-[16/11] w-full overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-5">
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] font-medium text-secondary-800">
                    {s.category}
                  </span>
                  <span className="text-xs text-foreground-400">
                    {s.date} · {s.readMin}
                  </span>
                </div>
                <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700">
                  展开全文 <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {current && (
        <StoryModal story={current} onClose={() => setOpenId(null)} />
      )}
    </section>
  );
}

function StoryModal({ story, onClose }: { story: FeatureStory; onClose: () => void }) {
  const paras = storyBody[story.id] || [story.excerpt];
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative flex max-h-[86vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-background-50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative h-44 w-full shrink-0 overflow-hidden md:h-56">
          <img src={story.image} alt={story.title} className="absolute inset-0 h-full w-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 to-transparent" />
          <button
            type="button"
            aria-label="关闭"
            onClick={onClose}
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-background-50/90 text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="overflow-y-auto p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] font-medium text-secondary-800">
              {story.category}
            </span>
            <span className="text-xs text-foreground-400">
              {story.date} · {story.readMin} 读完
            </span>
          </div>
          <h3 className="mt-3 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
            {story.title}
          </h3>
          <div className="mt-5 space-y-4">
            {paras.map((p, i) => (
              <p key={i} className="text-[15px] leading-8 text-foreground-700">
                {p}
              </p>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-2 border-t border-background-200 pt-4">
            {story.tags.map((tg) => (
              <span key={tg} className="rounded-md bg-background-200 px-2 py-1 text-xs text-foreground-600">
                #{tg}
              </span>
            ))}
          </div>
          <p className="mt-6 rounded-xl bg-background-100 p-4 text-xs leading-relaxed text-foreground-500">
            本文为宿野吃瓜编辑部深度专题，观点与数据整理自公开可检索信息，仅供交流参考，不作为事实定论。
          </p>
        </div>
      </div>
    </div>
  );
}